import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Heading, Image } from "@zillow/constellation";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import React__default from "react";
const animationsDefaultTimingDuration = "0.2s";
const ProgressBar = ({
  active,
  animationTime,
  skipAnimation
}) => {
  const BAR_WIDTH = "6px";
  const BORDER_RADIUS = "calc($radii.sm / 2)";
  const INACTIVE_COLOR = "$colors.gray200";
  const ACTIVE_COLOR = "$colors.brand";
  const [hasBeenActive, setHasBeenActive] = React__default.useState(active);
  React__default.useEffect(() => {
    if (active && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [active, hasBeenActive]);
  let css = {
    width: BAR_WIDTH,
    height: "100%",
    "background-color": !active && skipAnimation || !hasBeenActive ? INACTIVE_COLOR : ACTIVE_COLOR,
    "border-radius": BORDER_RADIUS
  };
  if (active && !skipAnimation) {
    css = {
      ...css,
      ...{
        animation: `progressAnimationStrike ${animationTime}s`,
        "animation-timing-function": "$animations.defaultTimingFunction",
        "@keyframes progressAnimationStrike": {
          from: {
            height: "0%"
          },
          to: {
            height: "100%"
          }
        }
      }
    };
  } else if (!active && !skipAnimation && hasBeenActive) {
    css = {
      width: BAR_WIDTH,
      "border-radius": BORDER_RADIUS,
      "background-color": "$colors.brand",
      animation: `inActiveProgressAnimationStrike ${animationsDefaultTimingDuration}`,
      "animation-timing-function": "$animations.defaultTimingFunction",
      "@keyframes inActiveProgressAnimationStrike": {
        from: {
          height: "100%"
        },
        to: {
          height: "0%"
        }
      }
    };
  }
  return /* @__PURE__ */ jsx(Box, { sx: {
    "background-color": INACTIVE_COLOR,
    "border-radius": BORDER_RADIUS
  }, children: /* @__PURE__ */ jsx(Box, { sx: css }) });
};
ProgressBar.propTypes = {
  active: PropTypes.bool,
  animationTime: PropTypes.number,
  skipAnimation: PropTypes.bool
};
const ListItemWithProgress = ({
  active,
  collapseOthers,
  animationTime,
  id,
  children,
  heading,
  onClick,
  skipAnimation
}) => {
  const divRef = React__default.useRef(null);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      onClick,
      sx: {
        cursor: "pointer",
        display: "flex",
        gap: "$spacing.md",
        outline: "none",
        "&:focus-visible": {
          boxShadow: "$shadows.focus"
        }
      },
      "aria-expanded": !collapseOthers || active,
      "aria-label": heading,
      role: "button",
      tabIndex: 0,
      ref: divRef,
      id,
      onKeyDown: (e) => {
        if (["Enter", "Space"].includes(e.key)) {
          e.preventDefault();
          onClick(divRef.current);
        }
      },
      children: [
        /* @__PURE__ */ jsx(ProgressBar, { animationTime, active, skipAnimation }),
        /* @__PURE__ */ jsxs(Box, { children: [
          /* @__PURE__ */ jsx(Heading, { as: "span", fontColor: active ? "brand" : "textDark", level: "5", children: heading }),
          /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", initial: false, children: (!collapseOthers || active) && /* @__PURE__ */ jsx(
            motion.div,
            {
              initial: {
                height: 0,
                opacity: 0
              },
              animate: {
                height: "auto",
                opacity: 1
              },
              exit: {
                height: 0,
                opacity: 0
              },
              style: {
                overflow: "hidden"
              },
              transition: {
                duration: 0.2,
                easing: "easeOut"
              },
              children
            },
            "content"
          ) })
        ] })
      ]
    }
  );
};
ListItemWithProgress.propTypes = {
  active: PropTypes.bool,
  heading: PropTypes.string,
  paragraph: PropTypes.string,
  animationTime: PropTypes.number,
  onClick: PropTypes.func,
  skipAnimation: PropTypes.bool,
  collapseOthers: PropTypes.bool
};
const imageCss = {
  objectFit: "contain",
  "animation-timing-function": "cubic - bezier(0.65, 0, 0.35, 1)",
  transition: `${animationsDefaultTimingDuration} $animations.defaultTimingFunction`,
  maxWidth: "100%",
  maxHeight: "100%"
};
const fadeInAnimation = {
  "@keyframes fadein": {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },
  animation: `fadein ${animationsDefaultTimingDuration}`
};
const fadeoutAnimation = {
  opacity: 0
};
const ImageWithProgress = ({
  active,
  media
}) => {
  const styles = {
    ...imageCss,
    ...active ? fadeInAnimation : fadeoutAnimation
  };
  return /* @__PURE__ */ jsx(Box, { as: Image, alt: imageAlt(media), src: imageSrc(media), width: media.width, height: media.height, sx: styles, "aria-hidden": !active });
};
ImageWithProgress.propTypes = {
  active: PropTypes.bool,
  media: PropTypes.object
};
({
  active: PropTypes.bool,
  media: PropTypes.object
});
function imageSrc(media, src) {
  return media ? media.src : src;
}
function imageAlt(media, alt) {
  return media && media.alt ? media.alt : alt;
}
export {
  ImageWithProgress,
  ListItemWithProgress,
  imageAlt,
  imageSrc
};
